import {
  mapCoord, 
  setOrigin, 
  midPoint, 
  distanceFromPoints,
  angleFromPoints,
  rgb,
  Coord
} from '../math/graphing'

let $

class RestoreStack {
  constructor (maxSize) {
    this.stack = []
    this.future = []

    this.maxSize = maxSize
  }
  current () {
    return this.stack[this.stack.length - 1]
  }
  back () {
    if (this.stack.length) {
      const last = this.stack.pop()
      this.future.push(last)
      return true
    } else {
      return false
    }
  }
  forward () {
    if (this.future.length) {
      const future = this.future.pop()
      this.stack.push(future)
      return true
    } else {
      return false
    }
  }
  add (item) {
    this.stack.push(item)
    this.future = []

    if (this.maxSize && this.stack.length > this.maxSize) {
      this.stack.shift()
    }
  }
}

export default class Pen {
  constructor (element, name) {
    $ = window.$
    this.el = element

    this.canvas = document.createElement('canvas')
    this.ctx = this.canvas.getContext('2d')
    this.$canvas = $(this.canvas)

    this.$canvas.css({
      pointerEvents: 'none',
      position: 'absolute'
    })
    
    this.el.append(this.canvas)
    
    if (name) { this.canvas.setAttribute('data-name', name) }
    
    this.resize()
    
    this.restoreStack = new RestoreStack(10)
    window.restoreStack = this.restoreStack

    this.q = []
    this.index = 0
    this.prevOpts = {}

    bind(this, 'destroy')
  }

  coordOnCanvas (coord) {
    return mapCoord(
      setOrigin(
        coord,
        this.offset
      ), 
      { width: this.canvasRect.width, height: this.canvasRect.height }, 
      { width: this.ctxWidth, height: this.ctxHeight }
    )
  }

  resize () {
    const dpr = Math.min(window.devicePixelRatio, 1.5) || 1

    let width = this.el.width()
    let height = this.el.height()

    this.offset = this.el.offset()
    const bonusOffset = Number(this.el.css('padding').replace('px', ''))

    this.offset.x = this.offset.left
    this.offset.y = this.offset.top

    width += bonusOffset * 2
    height += bonusOffset * 2

    this.elRect = {
      width,
      height,
      ...this.offset
    }

    this.$canvas.css({
      width,
      height,
      top: this.offset.top,
      left: 0
    })

    this.ctx.scale(dpr, dpr)
    this.canvas.width = width * dpr
    this.canvas.height = height * dpr

    this.ctxWidth = this.canvas.width
    this.ctxHeight = this.canvas.height

    this.canvasRect = this.elRect
  }

  pixels (amount) {
    return (amount / this.canvasRect.width) * this.canvas.width
  }

  clear () {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
  }

  circle (opts) {
    this.ctx.beginPath()
    this.ctx.arc(opts.x, opts.y, this.pixels(opts.r), 0, 2 * Math.PI)
  
    if (opts.fill) {
      this.ctx.fillStyle = opts.fill
      this.ctx.fill()
    }
  
    if (opts.stroke) {
      this.ctx.strokeStyle = opts.stroke
      this.ctx.stroke()
    }
  }

  pointsToCurve (opts) {
    const points = opts.points.map(coord => this.coordOnCanvas(coord))

    if (points.length < 2) return 

    this.ctx.lineJoin = 'round'
    this.ctx.lineCap = 'round'
    
    let p1 = points[0]
    let p2 = points[1]
    
    this.ctx.beginPath()
    this.ctx.moveTo(p1.x, p1.y)

    for (let i = 0; i < points.length; i++) {
      const currentMidPoint = midPoint(p1, p2)
      this.ctx.quadraticCurveTo(
        p1.x,
        p1.y,
        currentMidPoint.x,
        currentMidPoint.y
      )

      p1 = points[i]
      p2 = points[i + 1]
    }
    
    if (opts.lineWidth) {
      this.ctx.lineWidth = this.pixels(opts.lineWidth)
    }

    if (opts.fill) {
      this.ctx.fillStyle = opts.fill
      this.ctx.fill()
    }
  
    if (opts.stroke) {
      this.ctx.strokeStyle = opts.stroke
      this.ctx.stroke()
    }
  }

  withPaint (opts1) {
    return () => {
      if (opts1.blend) {
        this.ctx.globalCompositeOperation = opts1.blend
      }
    }
  }

  copy () {
    const canvas = document.createElement('canvas')
    
    canvas.width = this.ctxWidth
    canvas.height = this.ctxHeight
    
    const ctx = canvas.getContext('2d')

    ctx.drawImage(this.canvas, 0, 0)
    return canvas
  }

  image (image, x = 0, y = 0) {
    if (image) {
      this.ctx.drawImage(image, x, y)
    }
  }

  addRestore () {
    // Add restore point to stack (Just a copy of the entire canvas)
    this.restoreStack.add(
      this.copy()
    )
  }

  restoreLast () {
    this.restoreStack.back()
    const last = this.restoreStack.current()

    this.clear()
    if (last) {
      this.image(last)
    }
  }

  undo () {
    this.restoreLast()
  }

  restoreFuture () {
    this.restoreStack.forward()

    this.clear()
    this.image(this.restoreStack.current())
  }

  line (opts) {
    this.ctx.beginPath()
    const {x: x1, y: y1} = this.coordOnCanvas(opts.from)
    const {x: x2, y: y2} = this.coordOnCanvas(opts.to)

    this.ctx.moveTo(x1, y1)
    this.ctx.lineTo(x2, y2)

    if (opts.lineWidth) {
      this.ctx.lineWidth = this.pixels(opts.lineWidth)
    }

    if (opts.fill) {
      this.ctx.fillStyle = opts.fill
      this.ctx.fill()
    }
  
    if (opts.stroke) {
      this.ctx.strokeStyle = opts.stroke
      this.ctx.stroke()
    }
  }

  fill (opts) {
    const {point, size} = opts
    const fillColor = opts.fill

    const {x, y} = point
    const radGrad = this.ctx.createRadialGradient(x, y, size / 4, x, y, size / 2)
  
    // rgb will try to turn anything into a rgb compatible string
    radGrad.addColorStop(0, rgb(fillColor, 1).toString())
    radGrad.addColorStop(0.5, rgb(fillColor, 0.5).toString())
    radGrad.addColorStop(1, rgb(fillColor, 0).toString())
  }

  fillBetween (opts) {
    let {from, to, size, fill} = opts
    size = this.pixels(size)
    const angle = angleFromPoints(from, to)
    const distance = distanceFromPoints(from, to)

    for (let i = 0; i < distance; i += 2) {
      // Get current fill point
      const x1 = from.x + (Math.sin(angle) * i)
      const y1 = from.y + (Math.cos(angle) * i)

      const {x, y} = this.coordOnCanvas(new Coord(x1, y1))

      const radGrad = this.ctx.createRadialGradient(x, y, size / 4, x, y, size / 2)
  
      // rgb will try to turn anything into a rgb compatible string
      radGrad.addColorStop(0, rgb(fill, 1).toString())
      radGrad.addColorStop(0.5, rgb(fill, 0.5).toString())
      radGrad.addColorStop(1, rgb(fill, 0).toString())

      this.ctx.fillStyle = radGrad
      this.ctx.fillRect(x - size / 2, y - size / 2, size, size)
    }
  }

  queue (...items) {
    for (const item of items) {
      this.q.push(item)
    }
  }

  destroy () {
    this.$canvas.remove()
  }

  render (...fns) {
    if (fns.length) {
      this.queue(...fns)
    }

    if (this.raf) return

    this.raf = window.requestAnimationFrame(() => {
      const queue = this.q
      this.q = []

      for (const fn of queue) {
        if (isFunction(fn)) {
          fn(this)
        } else if (isFunction(fn.render)) {
          fn.render(this)
        }
      }

      this.raf = false
    })
  }
}

function isFunction (x) {
  return typeof x === 'function'
}

function containsTruthy (items) {
  for (const item of items) {
    if (item) return item
  }

  return false
}

function bind (self, ...els) {
  els.forEach(el => {
    self[el] = self[el].bind(self)
  })
}
